/**
 * Call API service for WebSocket-based voice calling
 * Matches the qcallai-app backend API pattern
 */

import type { AssistantDetailsResponse } from '../types/widget';

const DEFAULT_API_BASE_URL = 'https://api.qcall.ai/api/v1';

/**
 * Fetch assistant details (agent name, image, widget config) by assistant ID
 */
export async function fetchAssistantDetails(
  assistantId: string,
  apiKey: string,
  baseUrl: string = DEFAULT_API_BASE_URL
): Promise<AssistantDetailsResponse> {
  const response = await fetch(`${baseUrl}/user/getAssistant?assistant_id=${assistantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch assistant details: ${errorText}`);
  }

  const result = await response.json();

  // API wraps response in { success, status, message, data: [...] }
  if (result.success && Array.isArray(result.data) && result.data.length > 0) {
    return result.data[0];
  }

  // Fallback: return as-is
  return result;
}

/**
 * Response from initializing a call session
 */
export interface CallSessionResponse {
  temp_call_history_id: string;
  uuid: string;
  language: string;
  voice_modal: string;
  voice_name: string;
  start_speech?: string;
  is_back_sound: boolean;
  voice_speed: number;
  recorded_audio?: string;
  is_wait_start_speech?: boolean;
}

/**
 * Initialize a call session - creates a temporary call history entry
 */
export async function initCallSession(
  options: {
    assistant_id: string;
    userId?: string;
    workspace_id?: string;
  },
  apiKey?: string,
  baseUrl: string = DEFAULT_API_BASE_URL
): Promise<CallSessionResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const response = await fetch(`${baseUrl}/create-temp-call-data`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      assistant_id: options.assistant_id,
      userId: options.userId,
      workspace_id: options.workspace_id,
      mode: 'widget',
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to initialize call session: ${errorText}`);
  }

  const data = await response.json();

  // API wraps response in { success, payload }
  if (data.success && data.payload) {
    return data.payload;
  }

  return data;
}

/**
 * Finalize a call session - updates call history with recording and duration
 */
export async function finalizeCallSession(
  options: {
    call_sid: string;
    call_duration: number;
    recording_url?: string;
    userId?: string;
    temp_call_history_id?: string;
  },
  apiKey?: string,
  baseUrl: string = DEFAULT_API_BASE_URL
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const response = await fetch(`${baseUrl}/web-call`, {
    method: 'POST',
    headers,
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to finalize call session: ${errorText}`);
  }
}

/**
 * Upload a recording blob and get URL
 */
export async function uploadRecording(
  blob: Blob,
  callSid: string,
  apiKey?: string,
  baseUrl: string = DEFAULT_API_BASE_URL
): Promise<string> {
  const formData = new FormData();
  formData.append('file', blob, `${callSid}.wav`);
  formData.append('call_sid', callSid);

  const headers: Record<string, string> = {};
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const response = await fetch(`${baseUrl}/upload-recording`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload recording: ${errorText}`);
  }

  const data = await response.json();
  return (
    data?.data?.recording_url || ''
  );
}
